/**
 * useUpdateManagedSqlDb Hook
 *
 * Custom React Query mutation hook for updating managed SQL databases.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { managedSqlDbService } from '../../../api/managed-sql-db.service';
import type { ManagedSqlDbUpdateDTO } from '../../../types/managed-sql-db.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UpdateManagedSqlDbParams {
  managedSqlDbId: string;
  data: ManagedSqlDbUpdateDTO;
}

interface UseUpdateManagedSqlDbOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for updating managed SQL database
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useUpdateManagedSqlDb = (tenantId: string, options?: UseUpdateManagedSqlDbOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ managedSqlDbId, data }: UpdateManagedSqlDbParams) =>
      managedSqlDbService.updateManagedSqlDb(tenantId, managedSqlDbId, data),
    onSuccess: (_, variables) => {
      // Invalidate the list query
      queryClient.invalidateQueries({
        queryKey: ['managedSqlDbs', tenantId],
        exact: false,
      });
      // Invalidate the specific database details query
      queryClient.invalidateQueries({
        queryKey: ['managedSqlDbs', tenantId, variables.managedSqlDbId],
      });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
